import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ChevronDown } from "lucide-react";

export default function Terms() {
  const [expanded, setExpanded] = React.useState(null);

  const toggleSection = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  const sections = [
    {
      id: 1,
      title: "1. Deposit Protection",
      content: "Orello holds all rental deposits in a protected nodal account as per applicable regulations. These funds are not accessible to the landlord or tenant until a settlement is mutually agreed or a dispute is formally resolved through Orello's process. Orello acts as a neutral custodian and does not earn interest on held deposits. Deposits are transferred within 5-7 business days of settlement agreement or dispute resolution."
    },
    {
      id: 2,
      title: "2. Agreement & Signing",
      content: "The agreement PDF generated and signed through Orello is the sole binding record of the tenancy. All parties must digitally sign the agreement for it to be valid. Once signed by all required parties (landlord, tenants, and agent if applicable), the agreement is permanently locked and cannot be altered by anyone including Orello staff. No verbal modifications, WhatsApp messages, or external documents override the signed agreement."
    },
    {
      id: 3,
      title: "3. Dispute Resolution",
      content: "All deposit disputes must go through Orello's structured resolution process before external legal action is taken. The process includes: (1) AI-assisted assessment of claims, (2) optional human expert review, and (3) external escalation if needed. By using Orello, all parties agree to this sequential process. Final resolution decisions are binding and enforceable through legal mechanisms available under Indian law."
    },
    {
      id: 4,
      title: "4. User Responsibilities",
      content: "Users must provide accurate information during signup and agreement creation. You are responsible for maintaining the confidentiality of your login credentials. Users agree to use Orello only for lawful purposes and in compliance with all applicable laws. Any misuse, fraud, or violation of these terms may result in account suspension or termination without refund."
    },
    {
      id: 5,
      title: "5. Fees & Charges",
      content: "Orello charges a one-time platform fee on deposit collection. The fee structure is: 0.5% for deposits up to ₹1,50,000, 0.7% for deposits up to ₹15,00,000, and 1% for deposits above ₹15,00,000, with a minimum of ₹250. Fees are deducted at the time of deposit collection and are non-refundable once the deposit is successfully processed. Additional charges may apply for optional services such as dispute resolution mediation."
    },
    {
      id: 6,
      title: "6. Limitation of Liability",
      content: "Orello provides the platform 'as-is' without warranties. We are not liable for indirect, incidental, or consequential damages arising from your use of Orello. Our total liability shall not exceed the fees paid in the last 12 months. This includes but is not limited to loss of data, business interruption, or loss of profits. Users are responsible for their own backups and data management."
    },
    {
      id: 7,
      title: "7. Intellectual Property",
      content: "All content, design, and functionality of Orello are owned by Orello or licensed partners. Users may not reproduce, distribute, or transmit any content without explicit permission. Your data uploaded to Orello (agreements, signatures, ID documents) remains your property, but you grant Orello a license to use it for service delivery and legal compliance."
    },
    {
      id: 8,
      title: "8. Termination & Account Closure",
      content: "Orello reserves the right to suspend or terminate accounts that violate these terms. Upon account closure, your data is retained as required by law for a period of 7 years for tax and legal compliance. Personal information is deleted per the Privacy Policy. Active disputes or pending settlements must be resolved before account closure."
    },
    {
      id: 9,
      title: "9. Changes to Terms",
      content: "Orello may update these terms at any time. Significant changes will be notified via email at least 30 days in advance. Continued use of Orello after changes constitute acceptance of new terms. It is your responsibility to review terms periodically. If you disagree with updated terms, you may close your account before the changes take effect."
    },
    {
      id: 10,
      title: "10. Governing Law & Dispute Resolution",
      content: "These terms are governed by the laws of India. Any disputes arising from these terms or your use of Orello shall be subject to jurisdiction of courts in India. Before initiating legal proceedings, parties agree to attempt mediation. All notices and legal communications must be sent to legal@orello.in or as otherwise specified on the website."
    }
  ];

  return (
    <div style={{ background: "#080808", color: "#F5F5F5", minHeight: "100vh" }}>
      <Header />

      {/* Hero Section */}
      <section className="py-24 px-6" style={{ background: "linear-gradient(135deg, rgba(249,115,22,0.08), rgba(139,92,246,0.08))" }}>
        <div className="max-w-4xl mx-auto">
          <h1 className="font-display font-bold mb-4" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)", color: "#F5F5F5" }}>
            Terms & Conditions
          </h1>
          <p style={{ fontSize: "1.05rem", color: "#D1D5DB", lineHeight: 1.7, marginBottom: "1rem" }}>
            Last updated: June 2026
          </p>
          <p style={{ fontSize: "1rem", color: "#A3A3A3" }}>
            Please read these terms carefully. By using Orello, you agree to be bound by these terms.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-3">
            {sections.map((section) => (
              <div
                key={section.id}
                style={{
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: "12px",
                  background: "rgba(255,255,255,0.02)",
                  overflow: "hidden",
                  transition: "all 0.3s ease"
                }}
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  style={{
                    width: "100%",
                    padding: "1.5rem",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: expanded === section.id ? "rgba(249,115,22,0.04)" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => e.target.style.background = "rgba(249,115,22,0.04)"}
                  onMouseLeave={(e) => e.target.style.background = expanded === section.id ? "rgba(249,115,22,0.04)" : "transparent"}
                >
                  <h3 style={{
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: "#F5F5F5",
                    fontFamily: "'Poppins', sans-serif"
                  }}>
                    {section.title}
                  </h3>
                  <ChevronDown
                    style={{
                      width: 20,
                      height: 20,
                      color: "#F97316",
                      flexShrink: 0,
                      transition: "transform 0.3s ease",
                      transform: expanded === section.id ? "rotate(180deg)" : "rotate(0deg)"
                    }}
                  />
                </button>

                {expanded === section.id && (
                  <div style={{
                    padding: "0 1.5rem 1.5rem 1.5rem",
                    borderTop: "1px solid rgba(255,255,255,0.04)",
                    background: "rgba(249,115,22,0.02)"
                  }}>
                    <p style={{
                      fontSize: "0.95rem",
                      color: "#D1D5DB",
                      lineHeight: 1.8
                    }}>
                      {section.content}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div style={{
            marginTop: "3rem",
            padding: "2rem",
            borderRadius: "12px",
            background: "linear-gradient(135deg, rgba(249,115,22,0.08), rgba(139,92,246,0.08))",
            border: "1px solid rgba(249,115,22,0.12)"
          }}>
            <h3 style={{ fontSize: "1.1rem", fontWeight: 600, color: "#F5F5F5", marginBottom: "0.5rem" }}>
              Questions about these terms?
            </h3>
            <p style={{ fontSize: "0.95rem", color: "#D1D5DB" }}>
              Contact our legal team at{" "}
              <a href="mailto:legal@orello.in" style={{ color: "#F97316", textDecoration: "none", fontWeight: 600 }}>
                legal@orello.in
              </a>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}